import { useCallback, useMemo } from "react";
import type { BarChartEventProps } from "@/components/ui/bar-chart/BarChart.types";
import BarChartWithStates from "@/components/ui/bar-chart/BarChartWithStates";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { cn } from "@/lib/utils";
import { addCommasToNumbers } from "@/lib/utils/prettyName";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import { useAnalysisParamsContext } from "@/store/news-feed/params/analysis-params.context";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";
import {
  getColorsForStackBy,
  getFormatterForPeriod,
  getStackByLabelsFromDictionary,
  normalizeStackByToEnum,
  type StackByKey,
} from "./feedVolumeGraphHelpers";
import type { FeedVolumeGraphPeriod } from "./NewsFeedVolumeGraph.types";
import NewsFeedVolumeGraphHeader from "./NewsFeedVolumeGraphHeader";
import NewsFeedVolumeGraphTooltip from "./NewsFeedVolumeGraphTooltip";
import { useNewsFeedVolumeChartData } from "./useNewsFeedVolumeGraphData";

const NewsFeedVolumeGraph = () => {
  const stackFeedVolumeBy = useFeedContentManagementStore(
    (state) => state.stackFeedVolumeBy,
  );
  const setStackFeedVolumeByRaw = useFeedContentManagementStore(
    (state) => state.setStackFeedVolumeBy,
  );

  const setStackFeedVolumeBy = (value: string | null) => {
    if (value) {
      const normalized = normalizeStackByToEnum(value);
      setStackFeedVolumeByRaw(normalized ?? value);
    } else {
      setStackFeedVolumeByRaw(value);
    }
  };
  const activeBar = useFeedContentManagementStore((state) => state.activeBar);
  const setActiveBar = useFeedContentManagementStore(
    (state) => state.setActiveBar,
  );
  const setFeedVolume = useFeedContentManagementStore(
    (state) => state.setFeedVolume,
  );
  const setChartSelection = useFeedContentManagementStore(
    (state) => state.setChartSelection,
  );
  const resetFeedVolumeView = useFeedContentManagementStore(
    (state) => state.resetFeedVolumeView,
  );

  const { feedVolume, fetching: isFetchingFeedVolume } =
    useNewsFeedVolumeChartData(stackFeedVolumeBy);

  const analysisParams = useAnalysisParamsContext();
  const periodFromStore = useNewsFeedParamsStore(
    (state) => state.period,
  ) as FeedVolumeGraphPeriod;
  const setPeriodFromStore = useNewsFeedParamsStore((state) => state.setPeriod);
  const navigateWithSearch = analysisParams?.navigateWithSearch;

  const period: FeedVolumeGraphPeriod = analysisParams
    ? (analysisParams.period ?? "day")
    : periodFromStore;
  const setPeriod = useCallback(
    (p: FeedVolumeGraphPeriod) => {
      if (navigateWithSearch) {
        navigateWithSearch({ period: p ?? "day" });
      } else {
        setPeriodFromStore(p);
      }
    },
    [navigateWithSearch, setPeriodFromStore],
  );

  const filterDict = useFilterDictionary((state) => state);

  const stackByLabels = useMemo(() => {
    if (!stackFeedVolumeBy) return [];
    return getStackByLabelsFromDictionary(stackFeedVolumeBy, filterDict);
  }, [stackFeedVolumeBy, filterDict]);

  const isLoading = isFetchingFeedVolume;

  const dynamicInterval =
    period === "day"
      ? (feedVolume?.length ?? 0) > 31
        ? Math.floor((feedVolume?.length ?? 0) / 24)
        : 0
      : 0;

  const stackBy = useMemo(() => {
    if (!stackFeedVolumeBy) {
      return ["volume"];
    }
    // Special case for sentiment: ensure consistent order ["Negative", "Neutral", "Positive"]
    // Needed for both v1 and v2 to override the .toReversed() behavior
    if (
      stackFeedVolumeBy === "sentiment" ||
      stackFeedVolumeBy === "SENTIMENT"
    ) {
      return ["Negative", "Neutral", "Positive"];
    }
    return stackByLabels.toReversed();
  }, [stackFeedVolumeBy, stackByLabels]);

  const handleBarClick = useCallback(
    (v: BarChartEventProps) => {
      const { eventType, categoryClicked, ...rest } = v || {};

      setFeedVolume(v && typeof v.volume === "number" ? v.volume : null);
      setActiveBar(
        v && categoryClicked
          ? { ...rest, category: categoryClicked }
          : undefined,
      );

      // Set chartSelection with enum value as key and enum key (not display label) as value
      if (v && categoryClicked && stackFeedVolumeBy) {
        const normalizedEnum = normalizeStackByToEnum(
          stackFeedVolumeBy,
        ) as ShareVoiceSidebarCategory | null;
        if (normalizedEnum) {
          // Reverse lookup: get enum key from display label using _keyMap
          // Find the data item that matches this bar (using startOfPeriod from payload)
          const startOfPeriod = (rest as { startOfPeriod?: string })
            ?.startOfPeriod;
          const dataItem = startOfPeriod
            ? feedVolume?.find((item) => item.startOfPeriod === startOfPeriod)
            : null;

          // Get the original enum key from _keyMap if available
          let enumKey = categoryClicked;
          if (dataItem && "_keyMap" in dataItem) {
            const keyMap = dataItem._keyMap as Record<string, string>;
            enumKey = keyMap[categoryClicked] ?? categoryClicked;
          }

          // Store as string in format "CATEGORY:VALUE" for GraphQL
          setChartSelection(`${normalizedEnum}:${enumKey}`);
        } else {
          setChartSelection(null);
        }
      } else {
        setChartSelection(null);
      }
    },
    [
      setFeedVolume,
      setActiveBar,
      setChartSelection,
      stackFeedVolumeBy,
      feedVolume,
    ],
  );

  return (
    <div
      className={cn(
        "flex w-full min-w-0 flex-col gap-9 pt-2 transition-all duration-300 ease-in-out",
      )}
    >
      <div className="flex justify-end">
        <NewsFeedVolumeGraphHeader
          period={period}
          resetView={resetFeedVolumeView}
          setPeriod={setPeriod}
          stackFeedVolumeBy={stackFeedVolumeBy}
          setStackFeedVolumeBy={setStackFeedVolumeBy}
        />
      </div>
      <BarChartWithStates
        key={stackFeedVolumeBy ?? "default"}
        className="h-80 transition-all duration-300 ease-in-out"
        isLoading={isLoading}
        data={feedVolume}
        index={"startOfPeriod"}
        categories={stackBy}
        onValueChange={(v) => handleBarClick(v)}
        colors={
          stackFeedVolumeBy
            ? getColorsForStackBy(stackFeedVolumeBy as StackByKey)?.toReversed()
            : ["violet"]
        }
        borderRadius={2}
        showLegend={!!stackFeedVolumeBy}
        intervalType={dynamicInterval}
        formatXAxisTick={(value) => {
          const item = feedVolume?.find((d) => d.startOfPeriod === value) || {
            startOfPeriod: "",
            endOfPeriod: "",
          };

          return getFormatterForPeriod(period, {
            startDate: item?.startOfPeriod as string,
            endDate: item?.endOfPeriod as string,
          });
        }}
        valueFormatter={(v) => (v ? addCommasToNumbers(v) : "0")}
        rotateXAxisTick={feedVolume?.length > 12}
        defaultActiveBar={activeBar}
        mode={stackFeedVolumeBy ? "solid" : "gradient"}
        type={stackFeedVolumeBy ? "stacked" : "default"}
        stackedBy={stackFeedVolumeBy ?? undefined}
        customTooltip={(props) => {
          return (
            <NewsFeedVolumeGraphTooltip
              {...props}
              stackedBy={stackFeedVolumeBy}
              period={period}
            />
          );
        }}
      />
    </div>
  );
};

export default NewsFeedVolumeGraph;
